import { useMemo } from 'react';
import { StanTable } from '@/components';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useRecipientsStore } from '../stores';
import { useRecipients } from '../queries';
import { getRecipientColumns, RECIPIENTS_PAGE_SIZE } from '../constants';
import { buildRecipientsApiParams, getEmptyStateMessage, getPaginationLabel } from '../logic';
import type { Recipient } from '../types';

export const RecipientsTable = () => {
    const { push } = useStanRouter();

    const currentPage = useRecipientsStore(state => state.currentPage);
    const pageSize = useRecipientsStore(state => state.pageSize);
    const transformedFilters = useRecipientsStore(state => state.transformedFilters);
    const filterValues = useRecipientsStore(state => state.filterValues);
    const setCurrentPage = useRecipientsStore(state => state.setCurrentPage);

    // Build API params
    const apiParams = useMemo(
        () => buildRecipientsApiParams(currentPage, pageSize, transformedFilters),
        [currentPage, pageSize, transformedFilters]
    );

    const { data, isLoading, isFetching } = useRecipients(apiParams);

    const recipients = data?.recipient_list || [];
    const totalPages = data?.total_pages || 0;

    const columns = useMemo(() => getRecipientColumns(), []);

    const totalRowsLabel = getPaginationLabel(recipients.length, totalPages, RECIPIENTS_PAGE_SIZE, currentPage);

    const { header: emptyHeader, subheader: emptySubheader } = getEmptyStateMessage(filterValues.length > 0);

    const handleRowClick = (recipient: Recipient) => {
        push(`/audience/details/${recipient.user_fan_id}`);
    };

    return (
        <StanTable
            columns={columns}
            data={recipients}
            isLoading={isLoading || isFetching}
            page={currentPage}
            totalPages={totalPages}
            totalRowsLabel={totalRowsLabel}
            onPageChange={setCurrentPage}
            onRowClick={handleRowClick}
            emptyHeader={emptyHeader}
            emptySubheader={emptySubheader}
            hideHeaderWhenEmpty
        />
    );
};
